/**
 * Settings mental map — single catalog for discovery, search, and navigation.
 * One logical home per setting; classify by the module it affects.
 * Extensible: add items here to get search, favorites, deep-links, permissions & audit.
 */

export type SettingsDomainId =
  | 'system'
  | 'security'
  | 'accounting'
  | 'inventory'
  | 'sales'
  | 'purchases'
  | 'customers'
  | 'suppliers'
  | 'production'
  | 'hr'
  | 'pos'
  | 'reports'
  | 'printing'
  | 'devices'
  | 'integrations'
  | 'tax'
  | 'currencies'
  | 'notifications'
  | 'backup'
  | 'advanced'
  /** @deprecated Use `security` — kept for deep-link compatibility */
  | 'users'
  /** @deprecated Use `customers` / `suppliers` */
  | 'partners'

/** Screen contexts used by quick-access buttons on operational pages */
export type SettingsScreenContext =
  | 'sales'
  | 'inventory'
  | 'purchases'
  | 'accounting'
  | 'printing'
  | 'users'
  | 'security'
  | 'reports'
  | 'devices'
  | 'tax'
  | 'currencies'
  | 'notifications'
  | 'hr'
  | 'production'
  | 'partners'
  | 'customers'
  | 'suppliers'
  | 'pos'
  | 'integrations'
  | 'backup'
  | 'advanced'
  | 'system'

/** Resolve legacy domain aliases used in URLs / deep-links */
export function resolveDomainId(id: string | null | undefined): SettingsDomainId | null {
  if (!id) return null
  if (id === 'users') return 'security'
  if (id === 'partners') return 'customers'
  return id as SettingsDomainId
}

export type SettingsScope = 'system' | 'company' | 'branch' | 'user' | 'document'

export type SettingsValueKind =
  | 'page'
  | 'boolean'
  | 'number'
  | 'text'
  | 'list'
  | 'mixed'

/** Capability required beyond path menu access */
export type SettingsCapability = 'view' | 'edit' | 'reset'

export interface SettingsLabel {
  ar: string
  en: string
  fr?: string
}

export interface SettingsDomain {
  id: SettingsDomainId
  label: SettingsLabel
  description: SettingsLabel
  /** Accent for domain cards (Tailwind color stem, e.g. teal, indigo) */
  accent: string
  order: number
  /** Requires password/PIN gate before browsing this domain */
  sensitive?: boolean
}

export interface SettingsItem {
  id: string
  label: SettingsLabel
  description: SettingsLabel
  domain: SettingsDomainId
  /** Logical subsection within the domain */
  section: SettingsLabel
  keywords: string[]
  module: string
  scope: SettingsScope
  valueKind: SettingsValueKind
  path: string
  /** Optional hash/anchor on the target page */
  anchor?: string
  relatedIds?: string[]
  requiresRestart?: boolean
  requiresAdmin?: boolean
  /** Re-auth (password/PIN) before opening this setting */
  sensitive?: boolean
  /**
   * Minimum menu permission level on `path`:
   * 1 = view, 2 = edit, 3 = delete/reset. Default 1.
   */
  minPermissionLevel?: number
  /** Screens that should prioritize this setting when deep-linking */
  screenContexts?: SettingsScreenContext[]
  licenseModule?: string
  defaultHint?: SettingsLabel
}

/** Map operational screen context → default settings domain */
export const SCREEN_CONTEXT_DOMAIN: Record<SettingsScreenContext, SettingsDomainId> = {
  sales: 'sales',
  inventory: 'inventory',
  purchases: 'purchases',
  accounting: 'accounting',
  printing: 'printing',
  users: 'security',
  security: 'security',
  reports: 'reports',
  devices: 'devices',
  tax: 'tax',
  currencies: 'currencies',
  notifications: 'notifications',
  hr: 'hr',
  production: 'production',
  partners: 'customers',
  customers: 'customers',
  suppliers: 'suppliers',
  pos: 'pos',
  integrations: 'integrations',
  backup: 'backup',
  advanced: 'advanced',
  system: 'system',
}

export const SETTINGS_DOMAINS: SettingsDomain[] = [
  {
    id: 'system',
    label: { ar: 'النظام', en: 'System', fr: 'Système' },
    description: {
      ar: 'الشركة، الفروع، التنسيقات، والمظهر',
      en: 'Company, branches, formats, and appearance',
      fr: 'Entreprise, succursales, formats et apparence',
    },
    accent: 'slate',
    order: 1,
    sensitive: true,
  },
  {
    id: 'accounting',
    label: { ar: 'المحاسبة', en: 'Accounting', fr: 'Comptabilité' },
    description: {
      ar: 'الحسابات، القيود، والسنوات المالية',
      en: 'Accounts, journals, and fiscal years',
      fr: 'Comptes, écritures et exercices fiscaux',
    },
    accent: 'emerald',
    order: 2,
  },
  {
    id: 'tax',
    label: { ar: 'الضرائب', en: 'Tax', fr: 'Taxes' },
    description: {
      ar: 'ضريبة القيمة المضافة والفوترة الإلكترونية',
      en: 'VAT and e-invoicing',
      fr: 'TVA et facturation électronique',
    },
    accent: 'lime',
    order: 3,
  },
  {
    id: 'currencies',
    label: { ar: 'العملات', en: 'Currencies', fr: 'Devises' },
    description: {
      ar: 'العملات المتعددة وأسعار الصرف',
      en: 'Multi-currency and exchange rates',
      fr: 'Multi-devises et taux de change',
    },
    accent: 'yellow',
    order: 4,
  },
  {
    id: 'inventory',
    label: { ar: 'المخزون', en: 'Inventory', fr: 'Inventaire' },
    description: {
      ar: 'المستودعات، التقييم، الباركود، والجرد',
      en: 'Warehouses, valuation, barcode, and stock take',
      fr: 'Entrepôts, valorisation, codes-barres et inventaire',
    },
    accent: 'teal',
    order: 5,
  },
  {
    id: 'sales',
    label: { ar: 'المبيعات', en: 'Sales', fr: 'Ventes' },
    description: {
      ar: 'الفواتير، الأسعار، والخصومات',
      en: 'Invoices, pricing, and discounts',
      fr: 'Factures, prix et remises',
    },
    accent: 'sky',
    order: 6,
  },
  {
    id: 'purchases',
    label: { ar: 'المشتريات', en: 'Purchases', fr: 'Achats' },
    description: {
      ar: 'دورة الشراء، الطلبات، والمرتجعات',
      en: 'Purchase cycle, orders, and returns',
      fr: 'Cycle d\'achat, commandes et retours',
    },
    accent: 'amber',
    order: 7,
  },
  {
    id: 'customers',
    label: { ar: 'العملاء', en: 'Customers', fr: 'Clients' },
    description: {
      ar: 'بطاقات العملاء، الائتمان، وتقييد المندوب',
      en: 'Customer cards, credit, and sales-rep restrictions',
      fr: 'Fiches clients, crédit et restrictions des représentants',
    },
    accent: 'rose',
    order: 8,
  },
  {
    id: 'suppliers',
    label: { ar: 'الموردون', en: 'Suppliers', fr: 'Fournisseurs' },
    description: {
      ar: 'بطاقات الموردين وشروط التعامل',
      en: 'Vendor cards and trading terms',
      fr: 'Fiches fournisseurs et conditions commerciales',
    },
    accent: 'orange',
    order: 9,
  },
  {
    id: 'hr',
    label: { ar: 'الموارد البشرية', en: 'Human Resources', fr: 'Ressources humaines' },
    description: {
      ar: 'الدوام، الإجازات، الرواتب، والحضور',
      en: 'Schedules, leave, payroll, and attendance',
      fr: 'Horaires, congés, paie et présence',
    },
    accent: 'cyan',
    order: 10,
  },
  {
    id: 'production',
    label: { ar: 'التصنيع', en: 'Manufacturing', fr: 'Fabrication' },
    description: {
      ar: 'أوامر الإنتاج، المواد، ومراحل التصنيع',
      en: 'Production orders, materials, and stages',
      fr: 'Ordres de production, matières et étapes',
    },
    accent: 'red',
    order: 11,
  },
  {
    id: 'pos',
    label: { ar: 'نقاط البيع', en: 'Point of Sale', fr: 'Point de vente' },
    description: {
      ar: 'شاشة الكاشير، العرض، وطرق الدفع',
      en: 'Cashier screen, display, and payment methods',
      fr: 'Écran caisse, affichage et modes de paiement',
    },
    accent: 'sky',
    order: 12,
  },
  {
    id: 'reports',
    label: { ar: 'التقارير', en: 'Reports', fr: 'Rapports' },
    description: {
      ar: 'التقارير، التصدير، والمظهر',
      en: 'Reports, export, and appearance',
      fr: 'Rapports, export et apparence',
    },
    accent: 'indigo',
    order: 13,
  },
  {
    id: 'printing',
    label: { ar: 'الطباعة', en: 'Printing', fr: 'Impression' },
    description: {
      ar: 'القوالب، الطابعات، والباركود',
      en: 'Templates, printers, and barcodes',
      fr: 'Modèles, imprimantes et codes-barres',
    },
    accent: 'fuchsia',
    order: 14,
  },
  {
    id: 'integrations',
    label: { ar: 'التكاملات', en: 'Integrations', fr: 'Intégrations' },
    description: {
      ar: 'منصات التوصيل والمتاجر والأتمتة',
      en: 'Delivery platforms, stores, and automation',
      fr: 'Plateformes de livraison, boutiques et automatisation',
    },
    accent: 'violet',
    order: 15,
  },
  {
    id: 'security',
    label: { ar: 'الأمان والصلاحيات', en: 'Security & Permissions', fr: 'Sécurité et permissions' },
    description: {
      ar: 'المستخدمون، الأدوار، الصلاحيات، والتدقيق',
      en: 'Users, roles, permissions, and audit',
      fr: 'Utilisateurs, rôles, permissions et audit',
    },
    accent: 'violet',
    order: 16,
    sensitive: true,
  },
  {
    id: 'notifications',
    label: { ar: 'الإشعارات', en: 'Notifications', fr: 'Notifications' },
    description: {
      ar: 'تنبيهات الاستحقاق والصلاحية والأصوات',
      en: 'Due-date, expiry alerts, and sounds',
      fr: 'Alertes d\'échéance, d\'expiration et sons',
    },
    accent: 'pink',
    order: 17,
  },
  {
    id: 'devices',
    label: { ar: 'الأجهزة', en: 'Devices', fr: 'Appareils' },
    description: {
      ar: 'مزامنة الأجهزة الطرفية والموبايل',
      en: 'Peripheral and mobile device sync',
      fr: 'Synchronisation des périphériques et du mobile',
    },
    accent: 'stone',
    order: 18,
  },
  {
    id: 'backup',
    label: { ar: 'النسخ الاحتياطي', en: 'Backup', fr: 'Sauvegarde' },
    description: {
      ar: 'النسخ الاحتياطي ونقاط الاستعادة',
      en: 'Database backup and restore points',
      fr: 'Sauvegarde de la base et points de restauration',
    },
    accent: 'slate',
    order: 19,
    sensitive: true,
  },
  {
    id: 'advanced',
    label: { ar: 'إعدادات متقدمة', en: 'Advanced', fr: 'Avancé' },
    description: {
      ar: 'SQL، مخطط القاعدة، وسلامة البيانات',
      en: 'SQL, schema, and data integrity tools',
      fr: 'SQL, schéma et intégrité des données',
    },
    accent: 'stone',
    order: 20,
    sensitive: true,
  },
]

/** Preferences page hosts many legacy toggles under one path */
const PREFS = '/settings/preferences'

export const SETTINGS_ITEMS: SettingsItem[] = [
  // —— System ——
  {
    id: 'sys-companies',
    label: { ar: 'الشركات', en: 'Companies' },
    description: { ar: 'بيانات الشركات وإدارة الشركات المتعددة', en: 'Company profiles and multi-company management' },
    domain: 'system',
    section: { ar: 'عام', en: 'General' },
    keywords: ['شركة', 'companies', 'company', 'مؤسسة'],
    module: 'system',
    scope: 'system',
    valueKind: 'page',
    path: '/companies',
  },
  {
    id: 'sys-company-settings',
    label: { ar: 'إعدادات الشركة التفصيلية', en: 'Company detail settings' },
    description: { ar: 'إعدادات شركة محددة (تخزين، بريد، …)', en: 'Per-company settings (storage, email, …)' },
    domain: 'system',
    section: { ar: 'عام', en: 'General' },
    keywords: ['شركة', 'company settings', 'إعدادات الشركة'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/companies',
  },
  {
    id: 'sys-branches',
    label: { ar: 'الفروع', en: 'Branches' },
    description: { ar: 'إدارة فروع الشركة', en: 'Manage company branches' },
    domain: 'system',
    section: { ar: 'عام', en: 'General' },
    keywords: ['فرع', 'فروع', 'branch', 'branches'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/branches',
    screenContexts: ['system'],
  },
  {
    id: 'sys-number-format',
    label: { ar: 'تنسيق الأرقام', en: 'Number format' },
    description: { ar: 'الفاصلة العشرية، فاصل الآلاف، والعملة الافتراضية', en: 'Decimals, thousands separator, default currency display' },
    domain: 'system',
    section: { ar: 'التنسيقات', en: 'Formats' },
    keywords: ['أرقام', 'number', 'format', 'فاصلة', 'آلاف', 'تنسيق'],
    module: 'system',
    scope: 'company',
    valueKind: 'mixed',
    path: PREFS,
    anchor: 'number-format',
    relatedIds: ['sys-date-format', 'acc-currency'],
  },
  {
    id: 'sys-date-format',
    label: { ar: 'تنسيق التاريخ', en: 'Date format' },
    description: { ar: 'شكل عرض التواريخ في الواجهة والتقارير', en: 'How dates appear in UI and reports' },
    domain: 'system',
    section: { ar: 'التنسيقات', en: 'Formats' },
    keywords: ['تاريخ', 'date', 'format', 'تقويم'],
    module: 'system',
    scope: 'company',
    valueKind: 'mixed',
    path: PREFS,
    anchor: 'date-format',
  },
  {
    id: 'sys-colors',
    label: { ar: 'ألوان النظام', en: 'System colors' },
    description: { ar: 'تخصيص ألوان الواجهة', en: 'Customize UI colors' },
    domain: 'system',
    section: { ar: 'المظهر', en: 'Appearance' },
    keywords: ['ألوان', 'colors', 'ثيم', 'theme', 'مظهر'],
    module: 'system',
    scope: 'user',
    valueKind: 'page',
    path: '/settings/colors',
  },
  {
    id: 'sys-sounds',
    label: { ar: 'الملاحظات الصوتية', en: 'Sound feedback' },
    description: { ar: 'أصوات النجاح والخطأ والتنبيه', en: 'Success, error, and alert sounds' },
    domain: 'notifications',
    section: { ar: 'الأصوات', en: 'Sounds' },
    keywords: ['صوت', 'sound', 'تنبيه', 'beep'],
    module: 'system',
    scope: 'user',
    valueKind: 'mixed',
    path: PREFS,
    anchor: 'sound-feedback',
    screenContexts: ['notifications'],
  },
  {
    id: 'sys-notifications',
    label: { ar: 'إشعارات الاستحقاق', en: 'Due-date notifications' },
    description: { ar: 'أيام التنبيه قبل الشيكات، الصلاحية، الأقساط، …', en: 'Days before checks, expiry, installments, …' },
    domain: 'notifications',
    section: { ar: 'الاستحقاق', en: 'Due dates' },
    keywords: ['إشعار', 'notification', 'تنبيه', 'شيك', 'صلاحية', 'قسط'],
    module: 'system',
    scope: 'company',
    valueKind: 'mixed',
    path: PREFS,
    anchor: 'notifications',
    screenContexts: ['notifications', 'system'],
  },
  {
    id: 'sys-backup',
    label: { ar: 'النسخ الاحتياطي', en: 'Database backup' },
    description: { ar: 'نسخ واستعادة قاعدة البيانات', en: 'Back up and restore the database' },
    domain: 'backup',
    section: { ar: 'النسخ', en: 'Backup' },
    keywords: ['نسخ', 'backup', 'احتياطي', 'database', 'قاعدة'],
    module: 'system',
    scope: 'system',
    valueKind: 'page',
    path: '/settings/database-backup',
    requiresAdmin: true,
    sensitive: true,
    minPermissionLevel: 3,
  },
  {
    id: 'sys-restore-points',
    label: { ar: 'نقاط الاستعادة', en: 'Restore points' },
    description: { ar: 'إنشاء والعودة لنقاط استعادة', en: 'Create and roll back restore points' },
    domain: 'backup',
    section: { ar: 'الاستعادة', en: 'Restore' },
    keywords: ['استعادة', 'restore', 'نقطة'],
    module: 'system',
    scope: 'system',
    valueKind: 'page',
    path: '/settings/restore-points',
    requiresAdmin: true,
    sensitive: true,
    minPermissionLevel: 3,
  },
  {
    id: 'sys-updates',
    label: { ar: 'إدارة التحديثات', en: 'Updates' },
    description: { ar: 'تحميل وتطبيق تحديثات النظام', en: 'Download and apply system updates' },
    domain: 'system',
    section: { ar: 'الصيانة', en: 'Maintenance' },
    keywords: ['تحديث', 'update', 'version', 'إصدار'],
    module: 'system',
    scope: 'system',
    valueKind: 'page',
    path: '/settings/updates',
    requiresAdmin: true,
    requiresRestart: true,
    sensitive: true,
  },
  {
    id: 'sys-about',
    label: { ar: 'حول النظام', en: 'About & changelog' },
    description: { ar: 'الإصدار وسجل التغييرات', en: 'Version and changelog' },
    domain: 'system',
    section: { ar: 'الصيانة', en: 'Maintenance' },
    keywords: ['حول', 'about', 'changelog', 'إصدار', 'version'],
    module: 'system',
    scope: 'system',
    valueKind: 'page',
    path: '/settings/about',
  },
  {
    id: 'sys-schema',
    label: { ar: 'مقارنة الموديل وقاعدة البيانات', en: 'Model vs database' },
    description: { ar: 'اكتشاف الفروقات وتطبيق إصلاحات آمنة', en: 'Detect schema drift and apply safe fixes' },
    domain: 'advanced',
    section: { ar: 'قاعدة البيانات', en: 'Database' },
    keywords: ['schema', 'موديل', 'قاعدة', 'مقارنة', 'migration'],
    module: 'system',
    scope: 'system',
    valueKind: 'page',
    path: '/settings/schema-comparison',
    requiresAdmin: true,
  },
  {
    id: 'sys-data-integrity',
    label: { ar: 'فحص وإصلاح البيانات', en: 'Data integrity' },
    description: { ar: 'فحص اتساق البيانات وإصلاح المشاكل', en: 'Check data consistency and repair issues' },
    domain: 'advanced',
    section: { ar: 'البيانات', en: 'Data' },
    keywords: ['سلامة', 'integrity', 'إصلاح', 'فحص'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/data-integrity',
  },
  {
    id: 'sys-cost-repair',
    label: { ar: 'إصلاح التكاليف', en: 'Cost repair' },
    description: { ar: 'إعادة بناء متوسط تكلفة المخزون وتكلفة المبيعات', en: 'Rebuild inventory average cost and COGS' },
    domain: 'advanced',
    section: { ar: 'المخزون', en: 'Inventory' },
    keywords: ['تكلفة', 'مخزون', 'إصلاح', 'cost', 'repair', 'cogs'],
    module: 'inventory',
    scope: 'company',
    valueKind: 'page',
    path: '/inventory/cost-repair',
    requiresAdmin: true,
    sensitive: true,
    minPermissionLevel: 3,
  },
  {
    id: 'sys-document-repair',
    label: { ar: 'إصلاح قيود ومخزون المستندات', en: 'Document journal repair' },
    description: { ar: 'إعادة توليد قيود فواتير البيع والشراء والمرتجعات ومناقلات وتسويات المخزون وتحديث الأرصدة', en: 'Regenerate journals for sales, purchases, returns, transfers and adjustments, and refresh stock' },
    domain: 'advanced',
    section: { ar: 'المخزون', en: 'Inventory' },
    keywords: ['قيد', 'فاتورة', 'مرتجع', 'مناقلة', 'تسوية', 'إصلاح', 'journal', 'invoice', 'repair'],
    module: 'inventory',
    scope: 'company',
    valueKind: 'page',
    path: '/inventory/document-repair',
    requiresAdmin: true,
    sensitive: true,
    minPermissionLevel: 3,
  },
  {
    id: 'sys-data-mgmt',
    label: { ar: 'تنظيف البيانات', en: 'Data cleansing' },
    description: { ar: 'حذف أو تنظيف بيانات محددة بحذر', en: 'Carefully clean or purge selected data' },
    domain: 'advanced',
    section: { ar: 'البيانات', en: 'Data' },
    keywords: ['تنظيف', 'cleanse', 'حذف', 'بيانات'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/data-management',
    requiresAdmin: true,
    sensitive: true,
    minPermissionLevel: 3,
  },
  {
    id: 'sys-import',
    label: { ar: 'نظام الاستيراد', en: 'Import system' },
    description: { ar: 'استيراد أصناف، عملاء، قيود، … من ملفات', en: 'Import items, customers, journals, … from files' },
    domain: 'system',
    section: { ar: 'البيانات', en: 'Data' },
    keywords: ['استيراد', 'import', 'excel', 'csv', 'ملف'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/import-system',
  },
  {
    id: 'sys-export',
    label: { ar: 'نظام التصدير', en: 'Export system' },
    description: { ar: 'تصدير البيانات خارج النظام', en: 'Export data out of the system' },
    domain: 'system',
    section: { ar: 'البيانات', en: 'Data' },
    keywords: ['تصدير', 'export', 'excel'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/export-system',
    relatedIds: ['reports-export'],
  },
  {
    id: 'sys-sql',
    label: { ar: 'مستكشف SQL', en: 'SQL explorer' },
    description: { ar: 'استعلام قاعدة البيانات للمطورين', en: 'Query the database (developers)' },
    domain: 'advanced',
    section: { ar: 'التطوير', en: 'Development' },
    keywords: ['sql', 'query', 'استعلام', 'قاعدة'],
    module: 'system',
    scope: 'system',
    valueKind: 'page',
    path: '/settings/sql-query',
    requiresAdmin: true,
    sensitive: true,
    minPermissionLevel: 4,
  },
  {
    id: 'sys-automation',
    label: { ar: 'محرك الأتمتة', en: 'Automation engine' },
    description: { ar: 'قواعد وإجراءات آلية', en: 'Automated rules and actions' },
    domain: 'integrations',
    section: { ar: 'الأتمتة', en: 'Automation' },
    keywords: ['أتمتة', 'automation', 'workflow', 'تلقائي'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/automation',
    requiresAdmin: true,
    sensitive: true,
  },
  {
    id: 'sys-dynamic-workflow',
    label: { ar: 'سير العمل الديناميكي', en: 'Dynamic Workflow' },
    description: { ar: 'محرك سير عمل عام لأي مستند أو إجراء في النظام', en: 'Generic workflow engine for any document or action' },
    domain: 'system',
    section: { ar: 'الأتمتة', en: 'Automation' },
    keywords: ['workflow', 'سير', 'اعتماد', 'approval', 'ديناميكي', 'dynamic'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/dynamic-workflow',
    requiresAdmin: true,
  },
  {
    id: 'sys-license',
    label: { ar: 'إدارة التراخيص', en: 'License management' },
    description: { ar: 'تفعيل ومراجعة ترخيص النظام', en: 'Activate and review the system license' },
    domain: 'system',
    section: { ar: 'الترخيص', en: 'Licensing' },
    keywords: ['ترخيص', 'license', 'تفعيل'],
    module: 'system',
    scope: 'system',
    valueKind: 'page',
    path: '/settings/license',
  },
  {
    id: 'sys-device-sync',
    label: { ar: 'مزامنة الأجهزة', en: 'Device sync' },
    description: { ar: 'مزامنة الأجهزة الطرفية والموبايل', en: 'Sync peripherals and mobile devices' },
    domain: 'devices',
    section: { ar: 'المزامنة', en: 'Sync' },
    keywords: ['مزامنة', 'sync', 'جهاز', 'device', 'موبايل'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/device-sync',
    screenContexts: ['devices'],
  },
  {
    id: 'sys-connected-devices',
    label: { ar: 'الأجهزة المتصلة', en: 'Connected devices' },
    description: { ar: 'عرض أجهزة SmartAccount وحجبها عن الـ API', en: 'View SmartAccount devices and block them from the API' },
    domain: 'devices',
    section: { ar: 'الأجهزة', en: 'Devices' },
    keywords: ['أجهزة', 'حجب', 'device', 'block', 'api', 'متصل'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/connected-devices',
    requiresAdmin: true,
    screenContexts: ['devices'],
  },
  {
    id: 'sys-tailscale-devices',
    label: { ar: 'أجهزة الشبكة — Tailscale', en: 'Tailscale devices' },
    description: { ar: 'مراقبة أجهزة Tailnet وحالة Smart Account عليها', en: 'Monitor Tailnet devices and Smart Account status' },
    domain: 'devices',
    section: { ar: 'الأجهزة', en: 'Devices' },
    keywords: ['tailscale', 'شبكة', 'أجهزة', 'vpn', 'متصل'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/tailscale-devices',
    requiresAdmin: true,
    screenContexts: ['devices'],
    relatedIds: ['sys-tailscale-toggle'],
  },
  {
    id: 'sys-tailscale-toggle',
    label: { ar: 'تفعيل مراقبة Tailscale', en: 'Enable Tailscale monitoring' },
    description: { ar: 'تشغيل أو إيقاف قراءة حالة أجهزة الشبكة من Tailscale API', en: 'Turn Tailscale device status checks on or off' },
    domain: 'devices',
    section: { ar: 'الأجهزة', en: 'Devices' },
    keywords: ['tailscale', 'إيقاف', 'تفعيل', 'مراقبة', 'شبكة'],
    module: 'system',
    scope: 'company',
    valueKind: 'boolean',
    path: '/tailscale-devices',
    requiresAdmin: true,
    screenContexts: ['devices'],
  },
  {
    id: 'sys-signals',
    label: { ar: 'الإشارات المستلمة', en: 'Received signals' },
    description: { ar: 'مراقبة الإشارات الواردة من التكاملات', en: 'Monitor inbound integration signals' },
    domain: 'integrations',
    section: { ar: 'المراقبة', en: 'Monitoring' },
    keywords: ['إشارة', 'signal', 'webhook', 'تكامل'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/signals',
  },
  {
    id: 'sys-activity-log',
    label: { ar: 'سجل النشاطات', en: 'Activity log' },
    description: { ar: 'تتبع نشاط المستخدمين في الواجهة', en: 'Track user activity in the UI' },
    domain: 'security',
    section: { ar: 'التدقيق', en: 'Audit' },
    keywords: ['نشاط', 'activity', 'log', 'سجل'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/activity-log',
    requiresAdmin: true,
  },
  {
    id: 'sys-admin-operations',
    label: { ar: 'مراقبة العمليات', en: 'Operations monitor' },
    description: { ar: 'داشبورد المدير لمشاهدة كل العمليات ومتى حدثت بشكل مباشر', en: 'Live admin dashboard of all operations and when they happened' },
    domain: 'security',
    section: { ar: 'التدقيق', en: 'Audit' },
    keywords: ['مراقبة', 'عمليات', 'مباشر', 'مدير', 'dashboard', 'operations', 'live', 'activity'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/admin/operations',
    requiresAdmin: true,
    sensitive: true,
  },
  {
    id: 'sys-audit-trail',
    label: { ar: 'سجل التغييرات', en: 'Audit trail' },
    description: { ar: 'تتبع تعديلات البيانات الحساسة', en: 'Track sensitive data changes' },
    domain: 'security',
    section: { ar: 'التدقيق', en: 'Audit' },
    keywords: ['تدقيق', 'audit', 'تغييرات', 'trail'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/audit-trail',
  },
  {
    id: 'sys-custom-fields',
    label: { ar: 'الحقول المخصصة', en: 'Custom fields' },
    description: { ar: 'حقول إضافية للأصناف والمستندات', en: 'Extra fields for items and documents' },
    domain: 'system',
    section: { ar: 'التخصيص', en: 'Customization' },
    keywords: ['حقول', 'custom', 'مخصص', 'field'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/item-custom-fields',
  },
  {
    id: 'sys-dropdown-fields',
    label: { ar: 'حقول القوائم المنسدلة', en: 'Dropdown fields' },
    description: { ar: 'حقول البحث والعرض في قوائم العملاء والأصناف والحسابات', en: 'Search and display fields for customers, items, accounts, and other dropdowns' },
    domain: 'system',
    section: { ar: 'التخصيص', en: 'Customization' },
    keywords: ['قائمة', 'dropdown', 'منسدلة', 'اختيار', 'حقول البحث', 'عرض', 'عملاء', 'أصناف'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/dropdown-fields',
  },
  {
    id: 'sys-general-attrs',
    label: { ar: 'خصائص عامة', en: 'General properties' },
    description: { ar: 'خصائص عامة قابلة لإعادة الاستخدام', en: 'Reusable general attributes' },
    domain: 'system',
    section: { ar: 'التخصيص', en: 'Customization' },
    keywords: ['خصائص', 'attributes', 'عامة'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/general-attributes',
  },
  {
    id: 'sys-doc-numbering',
    label: { ar: 'ترقيم المستندات', en: 'Document numbering' },
    description: { ar: 'سلاسل الترقيم للمستندات حسب النوع', en: 'Number series per document type' },
    domain: 'system',
    section: { ar: 'الترقيم', en: 'Numbering' },
    keywords: ['ترقيم', 'numbering', 'مسلسل', 'serial', 'مستند'],
    module: 'system',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/document-numbering',
    relatedIds: ['sys-doc-numbering-toggle'],
  },
  {
    id: 'sys-doc-numbering-toggle',
    label: { ar: 'تفعيل ترقيم المستندات', en: 'Enable document numbering' },
    description: { ar: 'تشغيل/إيقاف نظام الترقيم المركزي', en: 'Turn central document numbering on/off' },
    domain: 'system',
    section: { ar: 'الترقيم', en: 'Numbering' },
    keywords: ['ترقيم', 'numbering', 'تفعيل'],
    module: 'system',
    scope: 'company',
    valueKind: 'boolean',
    path: PREFS,
    anchor: 'document-numbering',
  },
  {
    id: 'sys-eyewear',
    label: { ar: 'نظام النظارات / الوصفات', en: 'Eyewear prescriptions' },
    description: { ar: 'تفعيل قسم وصفات النظر في فاتورة المبيعات', en: 'Enable eyewear Rx section on sales invoices' },
    domain: 'system',
    section: { ar: 'الوحدات الاختيارية', en: 'Optional modules' },
    keywords: ['نظارات', 'eyewear', 'وصفة', 'prescription', 'عيون'],
    module: 'eyewear',
    scope: 'company',
    valueKind: 'boolean',
    path: PREFS,
    anchor: 'eyewear',
    licenseModule: 'eyewear',
  },
  {
    id: 'sys-medicine',
    label: { ar: 'تتبع الأدوية / هيئة الغذاء والدواء', en: 'Medicine / SFDA tracking' },
    description: { ar: 'تفعيل تتبع الأدوية ومتطلبات SFDA', en: 'Enable medicine tracking and SFDA requirements' },
    domain: 'system',
    section: { ar: 'الوحدات الاختيارية', en: 'Optional modules' },
    keywords: ['دواء', 'medicine', 'sfda', 'هيئة', 'تتبع'],
    module: 'medicine',
    scope: 'company',
    valueKind: 'boolean',
    path: PREFS,
    anchor: 'medicine-tracking',
    licenseModule: 'medicine',
  },

  // —— Users ——
  {
    id: 'usr-users',
    label: { ar: 'المستخدمون', en: 'Users' },
    description: { ar: 'إنشاء وإدارة حسابات المستخدمين', en: 'Create and manage user accounts' },
    domain: 'security',
    section: { ar: 'الحسابات', en: 'Accounts' },
    keywords: ['مستخدم', 'users', 'حساب', 'login'],
    module: 'security',
    scope: 'company',
    valueKind: 'page',
    path: '/users',
  },
  {
    id: 'usr-roles',
    label: { ar: 'الأدوار', en: 'Roles' },
    description: { ar: 'تعريف أدوار النظام', en: 'Define system roles' },
    domain: 'security',
    section: { ar: 'الأدوار', en: 'Roles' },
    keywords: ['دور', 'roles', 'مجموعة'],
    module: 'security',
    scope: 'company',
    valueKind: 'page',
    path: '/roles',
  },
  {
    id: 'usr-permissions',
    label: { ar: 'إدارة الصلاحيات', en: 'Permissions' },
    description: { ar: 'صلاحيات القوائم للمستخدمين والأدوار', en: 'Menu permissions for users and roles' },
    domain: 'security',
    section: { ar: 'الصلاحيات', en: 'Permissions' },
    keywords: ['صلاحية', 'permission', 'قائمة', 'menu'],
    module: 'security',
    scope: 'company',
    valueKind: 'page',
    path: '/permissions',
  },
  {
    id: 'usr-action-perms',
    label: { ar: 'الصلاحيات التفصيلية', en: 'Action permissions' },
    description: { ar: 'صلاحيات الإجراءات (حفظ، حذف، طباعة، …)', en: 'Action-level permissions (save, delete, print, …)' },
    domain: 'security',
    section: { ar: 'الصلاحيات', en: 'Permissions' },
    keywords: ['صلاحية', 'action', 'تفصيلي', 'خصم', 'تعديل'],
    module: 'security',
    scope: 'company',
    valueKind: 'page',
    path: '/permissions/actions',
    relatedIds: ['sales-discounts'],
  },
  {
    id: 'usr-audit-logs',
    label: { ar: 'سجل العمليات', en: 'Operation audit logs' },
    description: { ar: 'سجل عمليات المستخدمين الأمنية', en: 'Security-oriented user operation logs' },
    domain: 'security',
    section: { ar: 'التدقيق', en: 'Audit' },
    keywords: ['سجل', 'audit', 'عمليات', 'log'],
    module: 'security',
    scope: 'company',
    valueKind: 'page',
    path: '/audit-logs',
  },

  // —— Accounting ——
  {
    id: 'acc-coa',
    label: { ar: 'دليل الحسابات', en: 'Chart of accounts' },
    description: { ar: 'هيكل الحسابات المحاسبية', en: 'Accounting account structure' },
    domain: 'accounting',
    section: { ar: 'الحسابات', en: 'Accounts' },
    keywords: ['حسابات', 'chart', 'دليل', 'coa', 'محاسبة'],
    module: 'accounting',
    scope: 'company',
    valueKind: 'page',
    path: '/chart-of-accounts',
  },
  {
    id: 'acc-cost-centers',
    label: { ar: 'مراكز الكلفة', en: 'Cost centers' },
    description: { ar: 'تعريف مراكز التكلفة', en: 'Define cost centers' },
    domain: 'accounting',
    section: { ar: 'الحسابات', en: 'Accounts' },
    keywords: ['كلفة', 'cost center', 'مركز'],
    module: 'accounting',
    scope: 'company',
    valueKind: 'page',
    path: '/cost-centers',
  },
  {
    id: 'acc-fiscal-years',
    label: { ar: 'السنوات المالية', en: 'Fiscal years' },
    description: { ar: 'فتح وإغلاق السنوات المالية', en: 'Open and close fiscal years' },
    domain: 'accounting',
    section: { ar: 'الفترات', en: 'Periods' },
    keywords: ['سنة', 'fiscal', 'مالية', 'فترة'],
    module: 'accounting',
    scope: 'company',
    valueKind: 'page',
    path: '/fiscal-years',
  },
  {
    id: 'acc-currencies',
    label: { ar: 'العملات', en: 'Currencies' },
    description: { ar: 'تعريف العملات المستخدمة', en: 'Define available currencies' },
    domain: 'currencies',
    section: { ar: 'التعريف', en: 'Definition' },
    keywords: ['عملة', 'currency', 'عملات'],
    module: 'accounting',
    scope: 'company',
    valueKind: 'page',
    path: '/currencies',
    relatedIds: ['acc-exchange', 'acc-currency'],
    screenContexts: ['currencies', 'accounting'],
  },
  {
    id: 'acc-exchange',
    label: { ar: 'أسعار الصرف', en: 'Exchange rates' },
    description: { ar: 'قائمة أسعار صرف العملات', en: 'Currency exchange rate list' },
    domain: 'currencies',
    section: { ar: 'الأسعار', en: 'Rates' },
    keywords: ['صرف', 'exchange', 'rate', 'سعر'],
    module: 'accounting',
    scope: 'company',
    valueKind: 'page',
    path: '/exchange-rates',
    screenContexts: ['currencies'],
  },
  {
    id: 'acc-currency',
    label: { ar: 'سياسات تعدد العملات', en: 'Multi-currency policies' },
    description: { ar: 'تفعيل تعدد العملات وتجاوز سعر المستند', en: 'Enable multi-currency and document rate override' },
    domain: 'currencies',
    section: { ar: 'السياسات', en: 'Policies' },
    keywords: ['عملة', 'multi currency', 'سعر', 'مستند'],
    module: 'accounting',
    scope: 'company',
    valueKind: 'boolean',
    path: PREFS,
    anchor: 'currency-settings',
    screenContexts: ['currencies'],
  },
  {
    id: 'acc-tax-included',
    label: { ar: 'الضريبة مشمولة في السعر', en: 'Tax included in price' },
    description: { ar: 'هل الأسعار تشمل الضريبة افتراضياً', en: 'Whether prices include tax by default' },
    domain: 'tax',
    section: { ar: 'الأسعار', en: 'Pricing' },
    keywords: ['ضريبة', 'tax', 'vat', 'مشمول', 'included'],
    module: 'accounting',
    scope: 'company',
    valueKind: 'boolean',
    path: PREFS,
    anchor: 'tax-included',
    screenContexts: ['tax', 'sales'],
  },
  {
    id: 'acc-e-invoice',
    label: { ar: 'الفوترة الإلكترونية', en: 'E-Invoice settings', fr: 'Paramètres de facturation électronique' },
    description: { ar: 'إعدادات الربط مع الفوترة الإلكترونية', en: 'E-invoicing integration settings', fr: 'Paramètres d\'intégration de la facturation électronique' },
    domain: 'tax',
    section: { ar: 'الفوترة الإلكترونية', en: 'E-Invoicing' },
    keywords: ['فوترة', 'e-invoice', 'zatca', 'إلكتروني', 'ضريبة'],
    module: 'accounting',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/e-invoice',
  },
  {
    id: 'acc-billing-license',
    label: { ar: 'تراخيص الفوترة', en: 'Billing license settings', fr: 'Paramètres de licence de facturation' },
    description: { ar: 'ربط زاتكا: الشهادة، CSID، واختبارات الامتثال', en: 'ZATCA onboarding: CSR, CSID, and compliance tests' },
    domain: 'accounting',
    section: { ar: 'الضرائب', en: 'Tax' },
    keywords: ['ترخيص', 'فوترة', 'billing', 'license'],
    module: 'accounting',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/billing-license',
  },
  {
    id: 'acc-journal-gen',
    label: { ar: 'توليد القيود تلقائياً', en: 'Auto journal generation' },
    description: {
      ar: 'قيود مخزون أول المدة، المناقلات، والتسويات',
      en: 'Journals for opening stock, transfers, and adjustments',
    },
    domain: 'accounting',
    section: { ar: 'القيود', en: 'Journals' },
    keywords: ['قيد', 'journal', 'ترحيل', 'أتمتة', 'مخزون'],
    module: 'accounting',
    scope: 'company',
    valueKind: 'boolean',
    path: PREFS,
    anchor: 'journal-entry-settings',
  },
  {
    id: 'acc-post-on-save',
    label: { ar: 'ترحيل القيود/السندات عند الحفظ', en: 'Post vouchers/journals on save' },
    description: { ar: 'الافتراضي لترحيل السند أو القيد مباشرة بعد الحفظ', en: 'Default to post voucher/journal immediately on save' },
    domain: 'accounting',
    section: { ar: 'الترحيل', en: 'Posting' },
    keywords: ['ترحيل', 'post', 'حفظ', 'سند', 'قيد'],
    module: 'accounting',
    scope: 'company',
    valueKind: 'boolean',
    path: PREFS,
    anchor: 'journal-entry-settings',
  },
  {
    id: 'acc-allow-edit-posted-journal',
    label: { ar: 'تعديل القيد بعد الترحيل', en: 'Edit journal after posting' },
    description: {
      ar: 'السماح بتعديل قيود اليومية حتى بعد اعتمادها/ترحيلها',
      en: 'Allow editing posted/approved journal entries',
    },
    domain: 'accounting',
    section: { ar: 'الترحيل', en: 'Posting' },
    keywords: ['تعديل', 'ترحيل', 'قيد', 'edit', 'posted', 'journal'],
    module: 'accounting',
    scope: 'company',
    valueKind: 'boolean',
    path: PREFS,
    anchor: 'journal-entry-settings',
    sensitive: true,
  },
  {
    id: 'acc-prevent-journal-after-first-movement',
    label: { ar: 'منع تاريخ القيد بعد أول حركة لعميل/مورد', en: 'Block journal date after first customer/vendor movement' },
    description: {
      ar: 'عند التفعيل تُمنع العملية إذا كان تاريخ القيد بعد أول حركة مسجّلة على حساب عميل أو مورد. عند التعطيل يُحفظ القيد دون هذا الفحص.',
      en: 'When enabled, saving is blocked if the journal date is after the first movement on a customer or vendor account. Disable to skip this check.',
    },
    domain: 'accounting',
    section: { ar: 'القيود', en: 'Journals' },
    keywords: [
      'قيد',
      'يومية',
      'عميل',
      'مورد',
      'أول حركة',
      'رصيد افتتاحي',
      'افتتاحي',
      'تاريخ',
      'journal',
      'opening',
      'first movement',
      'customer',
      'vendor',
    ],
    module: 'accounting',
    scope: 'company',
    valueKind: 'boolean',
    path: PREFS,
    anchor: 'journal-entry-settings',
  },
  {
    id: 'acc-payment-methods',
    label: { ar: 'طرق الدفع', en: 'Payment methods' },
    description: { ar: 'تعريف طرق الدفع وربطها بالحسابات', en: 'Define payment methods and linked accounts' },
    domain: 'accounting',
    section: { ar: 'المدفوعات', en: 'Payments' },
    keywords: ['دفع', 'payment', 'طريقة', 'نقد', 'شبكة'],
    module: 'accounting',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/payment-methods',
    relatedIds: ['sales-payment'],
  },

  // —— Inventory ——
  {
    id: 'inv-stores',
    label: { ar: 'المستودعات', en: 'Warehouses' },
    description: { ar: 'إدارة المستودعات وربطها بالفروع', en: 'Manage warehouses and branch links' },
    domain: 'inventory',
    section: { ar: 'المستودعات', en: 'Warehouses' },
    keywords: ['مستودع', 'مخزن', 'store', 'warehouse'],
    module: 'inventory',
    scope: 'branch',
    valueKind: 'page',
    path: '/stores',
    screenContexts: ['inventory'],
  },
  {
    id: 'inv-units',
    label: { ar: 'الوحدات', en: 'Units of measure' },
    description: { ar: 'وحدات القياس للأصناف', en: 'Item units of measure' },
    domain: 'inventory',
    section: { ar: 'الأصناف', en: 'Items' },
    keywords: ['وحدة', 'unit', 'uom', 'قياس'],
    module: 'inventory',
    scope: 'company',
    valueKind: 'page',
    path: '/items',
    screenContexts: ['inventory'],
  },
  {
    id: 'inv-categories',
    label: { ar: 'فئات الأصناف', en: 'Item categories' },
    description: { ar: 'تصنيف الأصناف', en: 'Classify items' },
    domain: 'inventory',
    section: { ar: 'الأصناف', en: 'Items' },
    keywords: ['فئة', 'category', 'تصنيف', 'صنف'],
    module: 'inventory',
    scope: 'company',
    valueKind: 'page',
    path: '/categories',
    screenContexts: ['inventory'],
  },
  {
    id: 'inv-barcode',
    label: { ar: 'الباركود والمسح', en: 'Barcode & scanning' },
    description: {
      ar: 'سلوك البحث بالباركود والكمية (مثل 5*باركود) في الفواتير والجرد',
      en: 'Barcode search behavior and qty prefix (e.g. 5*code) on invoices and stock take',
    },
    domain: 'inventory',
    section: { ar: 'الباركود', en: 'Barcode' },
    keywords: ['باركود', 'barcode', 'ماسح', 'scanner', 'قارئ', 'ميزان'],
    module: 'inventory',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/invoice-settings',
    relatedIds: ['sales-invoice-settings', 'inv-stock-take'],
  },
  {
    id: 'inv-valuation',
    label: { ar: 'طريقة تقييم المخزون', en: 'Inventory valuation method' },
    description: { ar: 'مستمر / دوري حسب وحدات الشركة', en: 'Perpetual / periodic per company modules' },
    domain: 'inventory',
    section: { ar: 'التقييم', en: 'Valuation' },
    keywords: ['تقييم', 'valuation', 'FIFO', 'متوسط', 'مستمر', 'دوري'],
    module: 'inventory',
    scope: 'company',
    valueKind: 'page',
    path: PREFS,
    anchor: 'inventory-method',
  },
  {
    id: 'inv-periodic-close',
    label: { ar: 'إقفال المخزون الدوري', en: 'Periodic inventory close' },
    description: { ar: 'جدولة وتفعيل الإقفال الدوري', en: 'Schedule and enable periodic closing' },
    domain: 'inventory',
    section: { ar: 'الجرد', en: 'Stock take' },
    keywords: ['إقفال', 'دوري', 'periodic', 'جرد', 'close'],
    module: 'inventory',
    scope: 'company',
    valueKind: 'mixed',
    path: PREFS,
    anchor: 'periodic-close',
  },
  {
    id: 'inv-stock-take',
    label: { ar: 'الجرد السريع / الذكي', en: 'Quick / smart stock take' },
    description: { ar: 'صفحات الجرد وضبط فروقات المخزون', en: 'Stock-take pages and variance posting' },
    domain: 'inventory',
    section: { ar: 'الجرد', en: 'Stock take' },
    keywords: ['جرد', 'stock take', 'inventory count', 'فروقات'],
    module: 'inventory',
    scope: 'branch',
    valueKind: 'page',
    path: '/smart-stock-take',
  },
  {
    id: 'inv-transfer',
    label: { ar: 'تحويلات المخزون', en: 'Stock transfers' },
    description: { ar: 'مناقلات بين المستودعات/الفروع', en: 'Transfers between warehouses/branches' },
    domain: 'inventory',
    section: { ar: 'التحويلات', en: 'Transfers' },
    keywords: ['مناقلة', 'تحويل', 'transfer', 'فرع'],
    module: 'inventory',
    scope: 'branch',
    valueKind: 'page',
    path: '/inventory/transfer',
  },
  {
    id: 'inv-opening',
    label: { ar: 'مخزون أول المدة', en: 'Opening stock' },
    description: { ar: 'إدخال أرصدة أول المدة', en: 'Enter opening inventory balances' },
    domain: 'inventory',
    section: { ar: 'الأرصدة', en: 'Balances' },
    keywords: ['أول المدة', 'opening', 'رصيد', 'مخزون'],
    module: 'inventory',
    scope: 'branch',
    valueKind: 'page',
    path: '/opening-stock-invoices',
  },
  {
    id: 'inv-bins',
    label: { ar: 'مواقع التخزين (Bins)', en: 'Warehouse bins' },
    description: { ar: 'مواقع داخل المستودع وربط الأصناف', en: 'In-warehouse locations and item links' },
    domain: 'inventory',
    section: { ar: 'المستودعات', en: 'Warehouses' },
    keywords: ['bin', 'موقع', 'رف', 'تخزين'],
    module: 'inventory',
    scope: 'branch',
    valueKind: 'page',
    path: '/warehouse-bins',
  },

  // —— Sales ——
  {
    id: 'sales-invoice-settings',
    label: { ar: 'إعدادات الفواتير', en: 'Invoice settings' },
    description: { ar: 'سلوك فواتير المبيعات والطباعة المرتبطة', en: 'Sales invoice behavior and related printing' },
    domain: 'sales',
    section: { ar: 'الفواتير', en: 'Invoices' },
    keywords: ['فاتورة', 'invoice', 'مبيعات', 'طباعة'],
    module: 'sales',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/invoice-settings',
    relatedIds: ['inv-barcode', 'reports-print'],
    screenContexts: ['sales', 'printing'],
    minPermissionLevel: 2,
  },
  {
    id: 'sales-payment',
    label: { ar: 'طرق الدفع للمبيعات', en: 'Sales payment methods' },
    description: { ar: 'طرق الدفع المستخدمة في نقاط البيع والفواتير', en: 'Payment methods used in POS and invoices' },
    domain: 'pos',
    section: { ar: 'المدفوعات', en: 'Payments' },
    keywords: ['دفع', 'payment', 'نقد', 'بطاقة', 'pos'],
    module: 'sales',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/payment-methods',
    relatedIds: ['sales-invoice-settings'],
    screenContexts: ['pos', 'sales'],
  },
  {
    id: 'sales-pos-display',
    label: { ar: 'عرض نقطة البيع', en: 'POS display' },
    description: { ar: 'خيارات عرض شاشة نقطة البيع', en: 'POS screen display options' },
    domain: 'pos',
    section: { ar: 'العرض', en: 'Display' },
    keywords: ['pos', 'نقطة بيع', 'كاشير', 'عرض'],
    module: 'sales',
    scope: 'company',
    valueKind: 'mixed',
    path: PREFS,
    anchor: 'pos-display',
    screenContexts: ['pos', 'sales'],
  },
  {
    id: 'sales-pos',
    label: { ar: 'نقطة البيع', en: 'Point of sale' },
    description: { ar: 'فتح شاشة نقطة البيع', en: 'Open the POS screen' },
    domain: 'pos',
    section: { ar: 'التشغيل', en: 'Operations' },
    keywords: ['pos', 'نقطة بيع', 'كاشير'],
    module: 'sales',
    scope: 'branch',
    valueKind: 'page',
    path: '/pos',
    screenContexts: ['pos'],
  },
  {
    id: 'sales-retail-pos',
    label: { ar: 'نقطة بيع التجزئة', en: 'Retail POS' },
    description: { ar: 'كاشير تجزئة سريع بدون طاولات أو مطبخ', en: 'Fast retail cashier without tables or kitchen' },
    domain: 'sales',
    section: { ar: 'التشغيل', en: 'Operations' },
    keywords: ['pos', 'تجزئة', 'retail', 'كاشير', 'نقطة بيع'],
    module: 'sales',
    scope: 'branch',
    valueKind: 'page',
    path: '/retail-pos',
    screenContexts: ['sales', 'pos'],
  },
  {
    id: 'sales-discounts',
    label: { ar: 'الخصومات وصلاحياتها', en: 'Discounts & permission' },
    description: {
      ar: 'سياسات الخصم تُضبط من إعدادات الفاتورة؛ صلاحية التعديل من الصلاحيات التفصيلية',
      en: 'Discount policy via invoice settings; edit rights via action permissions',
    },
    domain: 'sales',
    section: { ar: 'الخصومات', en: 'Discounts' },
    keywords: ['خصم', 'discount', 'صلاحية', 'حد'],
    module: 'sales',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/invoice-settings',
    relatedIds: ['usr-action-perms'],
  },
  {
    id: 'sales-credit',
    label: { ar: 'حدود الائتمان', en: 'Credit limits' },
    description: {
      ar: 'حدود الدين على بطاقة العميل؛ التحقق يظهر عند حفظ فاتورة آجلة',
      en: 'Credit limits on the customer card; checked when saving credit invoices',
    },
    domain: 'sales',
    section: { ar: 'الائتمان', en: 'Credit' },
    keywords: ['ائتمان', 'credit', 'حد', 'دين', 'آجل'],
    module: 'sales',
    scope: 'company',
    valueKind: 'page',
    path: '/customers',
    relatedIds: ['partners-customers'],
  },

  // —— Purchases ——
  {
    id: 'pur-orders',
    label: { ar: 'أوامر الشراء', en: 'Purchase orders' },
    description: { ar: 'دورة أوامر الشراء', en: 'Purchase order cycle' },
    domain: 'purchases',
    section: { ar: 'الطلبات', en: 'Orders' },
    keywords: ['شراء', 'purchase', 'أمر', 'طلب'],
    module: 'purchases',
    scope: 'company',
    valueKind: 'page',
    path: '/purchase-orders',
  },
  {
    id: 'pur-grn',
    label: { ar: 'استلام البضاعة', en: 'Goods receipt' },
    description: { ar: 'سندات استلام المشتريات', en: 'Purchase goods receipt notes' },
    domain: 'purchases',
    section: { ar: 'الاستلام', en: 'Receipt' },
    keywords: ['استلام', 'grn', 'بضاعة', 'مشتريات'],
    module: 'purchases',
    scope: 'branch',
    valueKind: 'page',
    path: '/goods-receipt',
  },
  {
    id: 'pur-vendors',
    label: { ar: 'الموردون', en: 'Vendors' },
    description: { ar: 'بطاقات الموردين وشروط التعامل', en: 'Vendor cards and trading terms' },
    domain: 'suppliers',
    section: { ar: 'البطاقات', en: 'Cards' },
    keywords: ['مورد', 'vendor', 'supplier'],
    module: 'purchases',
    scope: 'company',
    valueKind: 'page',
    path: '/vendors',
    relatedIds: ['partners-vendors'],
    screenContexts: ['suppliers', 'purchases'],
  },

  // —— Partners ——
  {
    id: 'partners-customers',
    label: { ar: 'العملاء', en: 'Customers' },
    description: { ar: 'بطاقات العملاء، المندوب، وحدود الدين', en: 'Customer cards, sales rep, credit limits' },
    domain: 'customers',
    section: { ar: 'البطاقات', en: 'Cards' },
    keywords: ['عميل', 'customer', 'مندوب', 'دين'],
    module: 'crm',
    scope: 'company',
    valueKind: 'page',
    path: '/customers',
  },
  {
    id: 'partners-restrict',
    label: { ar: 'تقييد العملاء حسب المندوب', en: 'Restrict customers by sales rep' },
    description: {
      ar: 'كل مستخدم يرى فقط عملاء مندوبه (ما لم يكن مديراً)',
      en: 'Each user sees only their sales-rep customers (unless admin)',
    },
    domain: 'customers',
    section: { ar: 'الخصوصية', en: 'Privacy' },
    keywords: ['مندوب', 'تقييد', 'عميل', 'restrict', 'sales rep'],
    module: 'crm',
    scope: 'company',
    valueKind: 'boolean',
    path: PREFS,
    anchor: 'customer-restrict',
  },
  {
    id: 'partners-vendors',
    label: { ar: 'الموردون', en: 'Vendors' },
    description: { ar: 'إدارة الموردين', en: 'Manage vendors' },
    domain: 'suppliers',
    section: { ar: 'البطاقات', en: 'Cards' },
    keywords: ['مورد', 'vendor', 'supplier'],
    module: 'crm',
    scope: 'company',
    valueKind: 'page',
    path: '/vendors',
    relatedIds: ['pur-vendors'],
  },

  // —— Integrations ——
  {
    id: 'int-talabat',
    label: { ar: 'تكامل طلبات (Talabat)', en: 'Talabat integration', fr: 'Intégration Talabat' },
    description: { ar: 'ربط منصة طلبات مع النظام', en: 'Connect Talabat with the system', fr: 'Connecter Talabat au système' },
    domain: 'integrations',
    section: { ar: 'التوصيل', en: 'Delivery' },
    keywords: ['talabat', 'طلبات', 'تكامل', 'توصيل'],
    module: 'integrations',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/talabat',
    screenContexts: ['integrations'],
  },
  {
    id: 'int-jahez',
    label: { ar: 'تكامل جاهز (Jahez)', en: 'Jahez integration', fr: 'Intégration Jahez' },
    description: { ar: 'ربط منصة جاهز مع النظام', en: 'Connect Jahez with the system', fr: 'Connecter Jahez au système' },
    domain: 'integrations',
    section: { ar: 'التوصيل', en: 'Delivery' },
    keywords: ['jahez', 'جاهز', 'تكامل', 'توصيل'],
    module: 'integrations',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/jahez',
    screenContexts: ['integrations'],
  },
  {
    id: 'int-salla',
    label: { ar: 'تكامل سلة (Salla)', en: 'Salla integration', fr: 'Intégration Salla' },
    description: { ar: 'ربط متجر سلة مع النظام', en: 'Connect Salla store with the system', fr: 'Connecter Salla au système' },
    domain: 'integrations',
    section: { ar: 'المتاجر', en: 'Stores' },
    keywords: ['salla', 'سلة', 'تكامل', 'متجر'],
    module: 'integrations',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/salla',
    screenContexts: ['integrations'],
  },
  {
    id: 'int-hungration',
    label: { ar: 'تكامل هنقرستيشن', en: 'Hungration integration', fr: 'Intégration Hungration' },
    description: { ar: 'ربط هنقرستيشن مع النظام', en: 'Connect Hungration with the system', fr: 'Connecter Hungration au système' },
    domain: 'integrations',
    section: { ar: 'التوصيل', en: 'Delivery' },
    keywords: ['hungration', 'هنقر', 'تكامل', 'توصيل'],
    module: 'integrations',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/hungration',
    screenContexts: ['integrations'],
  },
  {
    id: 'int-ai-providers',
    label: { ar: 'مزودات الذكاء الاصطناعي', en: 'AI Providers', fr: 'Fournisseurs IA' },
    description: {
      ar: 'ربط DeepSeek وChatGPT وClaude وGemini بمفاتيح مشفّرة',
      en: 'Connect DeepSeek, ChatGPT, Claude, and Gemini with encrypted keys',
    },
    domain: 'integrations',
    section: { ar: 'الذكاء الاصطناعي', en: 'Artificial intelligence' },
    keywords: ['ai', 'deepseek', 'openai', 'chatgpt', 'claude', 'gemini', 'token', 'توكن', 'ذكاء'],
    module: 'integrations',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/ai-providers',
    screenContexts: ['integrations'],
  },
  {
    id: 'int-ai-center',
    label: { ar: 'مركز الذكاء الاصطناعي', en: 'AI Center', fr: 'Centre IA' },
    description: {
      ar: 'اسأل النظام باللغة الطبيعية عن الفواتير والأرصدة والمخزون',
      en: 'Ask the system in natural language about invoices, balances, and stock',
    },
    domain: 'integrations',
    section: { ar: 'الذكاء الاصطناعي', en: 'Artificial intelligence' },
    keywords: ['ai', 'chat', 'محادثة', 'تحليل', 'فواتير', 'تقارير'],
    module: 'integrations',
    scope: 'company',
    valueKind: 'page',
    path: '/ai',
    screenContexts: ['integrations'],
  },

  // —— Production ——
  {
    id: 'prd-boms',
    label: { ar: 'قوائم المواد (BOM)', en: 'Bills of materials' },
    description: { ar: 'تعريف مكونات التصنيع', en: 'Define manufacturing components' },
    domain: 'production',
    section: { ar: 'المواد', en: 'Materials' },
    keywords: ['bom', 'مواد', 'تصنيع', 'مكون'],
    module: 'production',
    scope: 'company',
    valueKind: 'page',
    path: '/production/boms',
    licenseModule: 'production',
  },
  {
    id: 'prd-orders',
    label: { ar: 'أوامر الإنتاج', en: 'Production orders' },
    description: { ar: 'إصدار ومتابعة أوامر الإنتاج', en: 'Issue and track production orders' },
    domain: 'production',
    section: { ar: 'الأوامر', en: 'Orders' },
    keywords: ['إنتاج', 'production', 'أمر', 'تصنيع', 'هدر'],
    module: 'production',
    scope: 'branch',
    valueKind: 'page',
    path: '/production/orders',
    licenseModule: 'production',
  },

  // —— HR ——
  {
    id: 'hr-employees',
    label: { ar: 'الموظفون', en: 'Employees' },
    description: { ar: 'ملفات الموظفين', en: 'Employee records' },
    domain: 'hr',
    section: { ar: 'الملفات', en: 'Records' },
    keywords: ['موظف', 'employee', 'hr'],
    module: 'hr',
    scope: 'company',
    valueKind: 'page',
    path: '/hr/employees',
    licenseModule: 'hr',
  },
  {
    id: 'hr-schedules',
    label: { ar: 'جداول العمل', en: 'Work schedules' },
    description: { ar: 'تعريف ورديات وجداول الدوام', en: 'Define shifts and work schedules' },
    domain: 'hr',
    section: { ar: 'الدوام', en: 'Attendance' },
    keywords: ['دوام', 'schedule', 'وردية', 'حضور'],
    module: 'hr',
    scope: 'company',
    valueKind: 'page',
    path: '/hr/work-schedules',
    licenseModule: 'hr',
  },
  {
    id: 'hr-attendance',
    label: { ar: 'الحضور والانصراف', en: 'Attendance' },
    description: { ar: 'تسجيل ومتابعة الحضور', en: 'Record and review attendance' },
    domain: 'hr',
    section: { ar: 'الدوام', en: 'Attendance' },
    keywords: ['حضور', 'انصراف', 'بصمة', 'attendance', 'fingerprint'],
    module: 'hr',
    scope: 'company',
    valueKind: 'page',
    path: '/hr/attendance',
    licenseModule: 'hr',
  },
  {
    id: 'hr-attendance-app',
    label: { ar: 'تطبيق الحضور GPS', en: 'GPS attendance app' },
    description: { ar: 'تسجيل الحضور والانصراف من الجوال مع التحقق من الموقع', en: 'Mobile check-in and check-out with location verification' },
    domain: 'hr',
    section: { ar: 'الدوام', en: 'Attendance' },
    keywords: ['حضور', 'انصراف', 'موقع', 'GPS', 'جيوفنس', 'تطبيق'],
    module: 'hr',
    scope: 'company',
    valueKind: 'page',
    path: '/hr/attendance-app',
    licenseModule: 'hr',
  },
  {
    id: 'hr-attendance-sites',
    label: { ar: 'مواقع العمل', en: 'Work sites' },
    description: { ar: 'تحديد مواقع العمل ونطاق التسجيل على الخريطة', en: 'Define work sites and geofence radius on the map' },
    domain: 'hr',
    section: { ar: 'الدوام', en: 'Attendance' },
    keywords: ['موقع', 'خريطة', 'جيوفنس', 'GPS', 'فرع'],
    module: 'hr',
    scope: 'company',
    valueKind: 'page',
    path: '/hr/attendance-sites',
    licenseModule: 'hr',
  },
  {
    id: 'hr-attendance-app-devices',
    label: { ar: 'أجهزة تطبيق الحضور', en: 'Attendance app phones' },
    description: { ar: 'ربط هاتف الموظف ومنع تسجيل حضور حساب آخر، مع فك الربط عند تغيير الجهاز', en: 'Bind each phone to one employee and unbind when the device changes' },
    domain: 'hr',
    section: { ar: 'الدوام', en: 'Attendance' },
    keywords: ['جهاز', 'هاتف', 'بصمة', 'Face ID', 'ربط'],
    module: 'hr',
    scope: 'company',
    valueKind: 'page',
    path: '/hr/attendance-app-devices',
    licenseModule: 'hr',
  },
  {
    id: 'hr-attendance-devices',
    label: { ar: 'أجهزة البصمة', en: 'Fingerprint devices' },
    description: { ar: 'ربط ماكينات الحضور والانصراف ZKTeco و Hikvision', en: 'Connect ZKTeco and Hikvision attendance machines' },
    domain: 'hr',
    section: { ar: 'الدوام', en: 'Attendance' },
    keywords: ['بصمة', 'ماكينة', 'جهاز', 'ZKTeco', 'Hikvision', 'fingerprint'],
    module: 'hr',
    scope: 'company',
    valueKind: 'page',
    path: '/hr/attendance-devices',
    licenseModule: 'hr',
  },
  {
    id: 'hr-attendance-punches',
    label: { ar: 'سجل البصمات والبريك', en: 'Punches and breaks' },
    description: { ar: 'حركات البصمة والاستراحة من الأجهزة', en: 'Device punches and break intervals' },
    domain: 'hr',
    section: { ar: 'الدوام', en: 'Attendance' },
    keywords: ['بريك', 'استراحة', 'بصمة', 'punch', 'break'],
    module: 'hr',
    scope: 'company',
    valueKind: 'page',
    path: '/hr/attendance-punches',
    licenseModule: 'hr',
  },
  {
    id: 'hr-attendance-policies',
    label: { ar: 'سياسات الحضور', en: 'Attendance policies' },
    description: { ar: 'قواعد التأخير والغياب والبصمة', en: 'Lateness, absence, and biometric rules' },
    domain: 'hr',
    section: { ar: 'الدوام', en: 'Attendance' },
    keywords: ['سياسة', 'بصمة', 'تأخير', 'غياب'],
    module: 'hr',
    scope: 'company',
    valueKind: 'page',
    path: '/hr/attendance-policies',
    licenseModule: 'hr',
  },
  {
    id: 'hr-leave',
    label: { ar: 'طلبات الإجازة', en: 'Leave requests' },
    description: { ar: 'إدارة الإجازات', en: 'Manage leave requests' },
    domain: 'hr',
    section: { ar: 'الإجازات', en: 'Leave' },
    keywords: ['إجازة', 'leave', 'vacation'],
    module: 'hr',
    scope: 'company',
    valueKind: 'page',
    path: '/hr/leave-requests',
    licenseModule: 'hr',
  },
  {
    id: 'hr-payroll',
    label: { ar: 'الرواتب', en: 'Payroll' },
    description: { ar: 'مسير الرواتب', en: 'Payroll processing' },
    domain: 'hr',
    section: { ar: 'الرواتب', en: 'Payroll' },
    keywords: ['راتب', 'payroll', 'مسير', 'أجور'],
    module: 'hr',
    scope: 'company',
    valueKind: 'page',
    path: '/hr/payroll',
    licenseModule: 'hr',
  },

  // —— Reports ——
  {
    id: 'reports-print',
    label: { ar: 'قوالب وطباعة الفواتير', en: 'Invoice print templates' },
    description: { ar: 'إعدادات الطباعة ضمن إعدادات الفواتير', en: 'Print options inside invoice settings' },
    domain: 'printing',
    section: { ar: 'القوالب', en: 'Templates' },
    keywords: ['طباعة', 'print', 'قالب', 'template', 'شعار', 'ورق', 'طابعة'],
    module: 'reports',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/invoice-settings',
    screenContexts: ['printing', 'sales'],
  },
  {
    id: 'reports-export',
    label: { ar: 'التصدير', en: 'Export' },
    description: { ar: 'تصدير البيانات والتقارير', en: 'Export data and reports' },
    domain: 'reports',
    section: { ar: 'التصدير', en: 'Export' },
    keywords: ['تصدير', 'export', 'excel', 'pdf'],
    module: 'reports',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/export-system',
  },
  {
    id: 'reports-colors',
    label: { ar: 'ألوان التقارير / الواجهة', en: 'Report / UI colors' },
    description: { ar: 'تخصيص الألوان التي تظهر في الواجهة والتقارير', en: 'Customize colors used in UI and reports' },
    domain: 'reports',
    section: { ar: 'المظهر', en: 'Appearance' },
    keywords: ['ألوان', 'شعار', 'logo', 'مظهر'],
    module: 'reports',
    scope: 'company',
    valueKind: 'page',
    path: '/settings/colors',
    screenContexts: ['reports', 'system'],
  },
  {
    id: 'adv-page-settings',
    label: { ar: 'إعدادات الصفحات', en: 'Page settings' },
    description: {
      ar: 'إعدادات باسم كل صفحة للتحكم بسلوكها منفرداً (Page.PageName)',
      en: 'Per-page settings bags that control each screen (Page.PageName)',
    },
    domain: 'advanced',
    section: { ar: 'الصفحات', en: 'Pages' },
    keywords: [
      'صفحة',
      'page',
      'page settings',
      'إعدادات الصفحة',
      'Page.',
      'سلوك',
      'behavior',
    ],
    module: 'system',
    scope: 'company',
    valueKind: 'mixed',
    path: '/settings',
    screenContexts: ['system', 'advanced'],
    defaultHint: {
      ar: 'من أي صفحة اضغط زر الترس العائم لفتح إعداداتها',
      en: 'From any page, tap the floating gear to open its settings',
    },
  },
]

export function getDomain(id: SettingsDomainId | string): SettingsDomain | undefined {
  const resolved = resolveDomainId(id) || (id as SettingsDomainId)
  return SETTINGS_DOMAINS.find((d) => d.id === resolved)
}

export function getItemsByDomain(domain: SettingsDomainId): SettingsItem[] {
  const resolved = resolveDomainId(domain) || domain
  return SETTINGS_ITEMS.filter((i) => {
    const itemDomain = resolveDomainId(i.domain) || i.domain
    return itemDomain === resolved
  })
}

export function getItemById(id: string): SettingsItem | undefined {
  return SETTINGS_ITEMS.find((i) => i.id === id)
}

export function searchSettingsItems(query: string): SettingsItem[] {
  const q = query.trim().toLowerCase()
  if (!q) return []

  const tokens = q.split(/\s+/).filter(Boolean)

  const scoreItem = (item: SettingsItem): number => {
    const domain = getDomain(item.domain)
    const hay = [
      item.label.ar,
      item.label.en,
      item.description.ar,
      item.description.en,
      item.section.ar,
      item.section.en,
      item.module,
      domain?.label.ar || '',
      domain?.label.en || '',
      domain?.description.ar || '',
      domain?.description.en || '',
      ...item.keywords,
    ]
      .join(' ')
      .toLowerCase()

    let score = 0
    for (const token of tokens) {
      if (item.keywords.some((k) => k.toLowerCase() === token)) score += 12
      else if (item.keywords.some((k) => k.toLowerCase().includes(token))) score += 8
      else if (item.label.ar.toLowerCase().includes(token) || item.label.en.toLowerCase().includes(token))
        score += 10
      else if (domain && (domain.label.ar.toLowerCase().includes(token) || domain.label.en.toLowerCase().includes(token)))
        score += 7
      else if (hay.includes(token)) score += 4
      else return 0
    }
    return score
  }

  return SETTINGS_ITEMS.map((item) => ({ item, score: scoreItem(item) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.item.label.en.localeCompare(b.item.label.en))
    .map((x) => x.item)
}

export function settingsText(label: SettingsLabel, language: string): string {
  if (language === 'ar') return label.ar
  if (language === 'fr') return label.fr || label.en
  return label.en
}

export function groupItemsBySection(items: SettingsItem[], language: string): { section: string; items: SettingsItem[] }[] {
  const map = new Map<string, SettingsItem[]>()
  for (const item of items) {
    const key = settingsText(item.section, language)
    const list = map.get(key) || []
    list.push(item)
    map.set(key, list)
  }
  return Array.from(map.entries()).map(([section, sectionItems]) => ({ section, items: sectionItems }))
}

/** Items related to an operational screen — shown first when deep-linking */
export function getItemsByScreenContext(context: SettingsScreenContext): SettingsItem[] {
  const tagged = SETTINGS_ITEMS.filter((i) => i.screenContexts?.includes(context))
  if (tagged.length > 0) return tagged
  const domain = SCREEN_CONTEXT_DOMAIN[context]
  return getItemsByDomain(domain)
}

export function buildSettingsUrl(options: {
  domain?: SettingsDomainId | null
  query?: string
  itemId?: string
  context?: SettingsScreenContext
  /** Page-named settings id (e.g. Invoices, NewInvoice) */
  pageId?: string
}): string {
  const params = new URLSearchParams()
  const domain = resolveDomainId(options.domain) || options.domain
  if (domain) params.set('domain', domain)
  if (options.query) params.set('q', options.query)
  if (options.itemId) params.set('item', options.itemId)
  if (options.context) params.set('from', options.context)
  if (options.pageId) params.set('page', options.pageId)
  const qs = params.toString()
  return qs ? `/settings?${qs}` : '/settings'
}

/**
 * Capability from menu permission level:
 * 0 deny · 1 view · 2 edit · 3+ reset/delete
 */
export function capabilityFromLevel(level: number): SettingsCapability | 'deny' {
  if (level <= 0) return 'deny'
  if (level === 1) return 'view'
  if (level === 2) return 'edit'
  return 'reset'
}

export function itemMeetsPermission(item: SettingsItem, pathLevel: number, isAdmin: boolean): boolean {
  if (item.requiresAdmin && !isAdmin) return false
  const min = item.minPermissionLevel ?? 1
  if (isAdmin) return true
  return pathLevel >= min
}
